<script setup lang="ts">
import { watch } from 'vue';
import Bugsnag from '@bugsnag/js';
import { storeToRefs } from 'pinia';
import { RouterView, useRoute } from 'vue-router';
import { useDisplay } from 'vuetify';
import BreakpointViewer from '@/components/dev/BreakpointViewer.vue';
import SwitchUser from '@/components/dev/SwitchUser/SwitchUser.vue';
import TestApi from '@/components/dev/TestApi/TestApi.vue';
import HeaderBar from '@/features/header/HeaderBar.vue';
import MenuMobile from '@/features/mobile/drawer/Drawer.vue';
import NavigationMobile from '@/features/mobile/navigation/Navigation.vue';
import InvitationModal from '@/features/network/invitation-modal/InvitationModal.vue';
import MediaModal from '@/features/overlays/modals/media/MediaModal.vue';
import CVUploadModal from './features/overlays/modals/cv-upload/CVUploadModal.vue';
import { useModalStore } from '@/features/overlays/modals/modalStore';
import SharersMobile from '@/features/overlays/sharers/SharersMobile.vue';
import PrivacyModal from '@/features/privacy/PrivacyModal.vue';
import TypeformBtn from '@/features/typeform/TypeformBtn.vue';
import { useLocalesQuery } from '@/api/queries/locales/useLocalesQuery';
import useMeQuery from '@/api/queries/me/useMeQuery';
import { initMixpanel, MixpanelInstance, isMixpanelInitialized } from '@/plugins/mixpanel/mixpanel';
import { useGlobalStore } from '@/stores/global';
import { useLocaleStore } from '@/stores/locales';
import SidePanels from '@/views/side-panels/SidePanels.vue';
import '@/tailwind.css';
import { ConfigProvider } from 'reka-ui';
import CGUUpdateModal from './features/overlays/modals/CGUUpdateModal.vue';

//-- Variables
const { auth, permissions, me, meSuspense } = useMeQuery();
const { localesSuspense } = useLocalesQuery();
const { env } = storeToRefs(useGlobalStore());
const { currentLocale, currentLang, isRtl } = storeToRefs(useLocaleStore());
const { mobile, smAndDown } = useDisplay();
const modalStore = useModalStore();
const localesStore = useLocaleStore();
const route = useRoute();
const TOU_LIMIT_DATE = new Date('2025-12-15').getTime() / 1000;

/**
 * Init me and locale on each page
 */
async function init() {
  const [me] = await Promise.all([meSuspense(), localesSuspense()]);

  if (me.data && me.data.user.locale_id !== currentLocale.value.id) {
    await localesStore.updateLocale(me.data.user?.locale_id);
  }

  if (route.query?.lang) {
    const locale = useLocalesQuery().getLocaleByIsoCode(route.query.lang as string);
    if (locale) {
      await useLocaleStore().updateLocale(locale.id);
    }
  }

  await localesStore.loadNamespaces('global');

  document.documentElement.setAttribute('lang', localesStore.currentLocale.iso_code_639);
  document.documentElement.dir = isRtl.value ? 'rtl' : 'ltr';
  document.documentElement.style.setProperty('--v-locale--is-ltr', isRtl.value ? 'rtl' : 'ltr');
}

await init();

//-- Watchers

/**
 * Init Mixpanel
 * Mixpanel initialization cannot be done in main.ts as it requires auth user info and therefore me query.
 */
watch(
  auth,
  async (auth) => {
    if (auth) {
      const isRecruiter = permissions.value?.includes('rct_r');
      const isManager = permissions.value?.includes('mng_r');
      const userRole = isRecruiter ? 'Recruiter' : isManager ? 'Manager' : 'Candidate';
      await initMixpanel(auth.uuid, auth.email, userRole);

      if (isMixpanelInitialized()) {
        // On first load here because we need initMixpanel
        MixpanelInstance.track(`Enter ${route.name?.toString()}`, {
          entry_point: undefined,
        });
      }
    }

    if (route.meta.group === 'public' && !auth) {
      await initMixpanel(undefined, undefined, 'Unloggued');

      if (isMixpanelInitialized()) {
        // On first load here because we need initMixpanel
        MixpanelInstance.track(`Enter ${route.name?.toString()}`, {
          entry_point: undefined,
        });
      }
    }

    // Set Bugsnag user when auth user available
    if (auth && !Bugsnag.getUser().id) {
      Bugsnag.setUser(auth.uuid, auth.email, `${auth.first_name} ${auth.last_name}`);
    }
    if (me?.value?.consent) {
      const touConsent = me.value.consent.find((consent) => consent.type === 'tou');

      if (touConsent && touConsent.is_accepted && touConsent.updated_at < TOU_LIMIT_DATE) {
        modalStore.toggle('cguUpdate');
      }
    }
  },
  { immediate: true },
);
</script>

<template>
  <!-- Dev Components -->
  <template v-if="env === 'development'">
    <BreakpointViewer /> <SwitchUser /> <TestApi />
  </template>
  <ConfigProvider :dir="isRtl ? 'rtl' : 'ltr'">
    <v-app
      :key="currentLocale.iso_code_639"
      :style="{ '--v-locale--is-ltr': isRtl ? 'rtl' : 'ltr' }"
      :dir="isRtl ? 'rtl' : 'ltr'"
    >
      <template v-if="auth">
        <!-- Sharers mobile -->
        <SharersMobile v-if="auth && smAndDown" />
        <SidePanels />

        <!-- Global Modals -->
        <MediaModal />
        <CVUploadModal />
        <PrivacyModal />
        <InvitationModal v-model="modalStore.invitation.visible" />
        <CGUUpdateModal v-model="modalStore.cguUpdate.visible" />

        <MenuMobile v-if="mobile" />
        <TypeformBtn v-if="currentLang === 'fr' || currentLang === 'en'" />
        <HeaderBar />
      </template>

      <main class="w-full h-full">
        <RouterView v-slot="{ Component }">
          <v-scroll-y-transition mode="out-in">
            <component :is="Component"></component>
          </v-scroll-y-transition>
        </RouterView>
      </main>
      <NavigationMobile v-if="auth && mobile && route.name !== 'public_profile'" />
    </v-app>
  </ConfigProvider>
</template>

<style>
.v-locale--is-ltr {
  direction: var(--v-locale--is-ltr);
}
</style>
